import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReportsPage() {
  const { t } = useLanguage();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/business-reports/published`).then(r => setReports(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const businessReports = reports.filter(r => r.reportType === 'business');
  const financialReports = reports.filter(r => r.reportType === 'financial');

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">{t('contentPages.legal.loading')}</div>;

  const ReportList = ({ items, title }) => (
    <div className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
      {items.length === 0 ? (
        <p className="text-gray-400">{t('contentPages.reports.noItems')}</p>
      ) : (
        <div className="space-y-3">
          {items.map(r => (
            <div key={r.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-[#00A651]" />
                <div>
                  <p className="font-medium text-gray-900">{r.title}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar className="h-3 w-3" />{r.fiscalYear}
                  </div>
                </div>
              </div>
              {r.fileUrl && (
                <a href={r.fileUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-1" />{t('contentPages.reports.download')}
                  </Button>
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="reports-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('contentPages.reports.title')}</h1>
          <p className="text-gray-500">{t('contentPages.reports.subtitle')}</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {reports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-400">
            {t('contentPages.reports.noData')}
          </div>
        ) : (
          <>
            <ReportList items={businessReports} title={t('contentPages.reports.businessReports')} />
            <ReportList items={financialReports} title={t('contentPages.reports.financialReports')} />
          </>
        )}
      </div>
    </div>
  );
}
